import { JSX, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Button, Col, DatePicker, Flex, Form, Input, Row, Select } from "antd"
import { useForm } from "antd/es/form/Form"
import {
  CloseCircleOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons"

import { useDashboardLayout } from "@src/app/layouts/dashboardLayout"
import {
  FIELDS_TYPE,
  FiltersFieldSelectType,
  FiltersFieldType,
  TableFilterProps,
} from "@src/shared/componemts/TableFilter/types"

export const TableFilters = observer(
  ({ clearAll = false, fields, onChange, state }: TableFilterProps) => {
    const [form] = useForm()
    const { layout } = useDashboardLayout()
    const [isExpanded, setIsExpanded] = useState(false)
    const collapsedFieldsCount = Math.floor(24 / layout.filterSpan)
    const visibleFields = useMemo(
      () => (isExpanded ? fields : fields.slice(0, collapsedFieldsCount)),
      [collapsedFieldsCount, fields, isExpanded],
    )

    const hasHiddenFields = fields.length > collapsedFieldsCount

    useEffect(() => {
      if (state.id) {
        form.setFieldsValue({ id: state.id })
      }
    }, [state])

    const getField = (i: FiltersFieldType): JSX.Element => {
      const commonProps = {
        ...(i.placeholder ? { placeholder: i.placeholder } : {}),
        ...(i.isLoading !== undefined ? { loading: i.isLoading } : {}),
      }

      const FIELDS_MAP = {
        [FIELDS_TYPE.DATE]: (
          <DatePicker.RangePicker
            onChange={(e) => change({ ...form.getFieldsValue(), [i.name]: e })}
            showTime
            style={{ width: "100%" }}
          />
        ),
        [FIELDS_TYPE.INPUT]: (
          <Input
            {...commonProps}
            onChange={(e) => onSearch(e.target.value, i)}
          />
        ),
        [FIELDS_TYPE.SEARCH]: (
          <Input.Search
            {...commonProps}
            allowClear
            defaultValue={state[i.name]}
            enterButton
            id={i.name}
            loading={false}
            onSearch={(e) => onSearch(e, i)}
            value={state[i.name]}
          />
        ),
        [FIELDS_TYPE.SELECT]: (
          <Select
            {...commonProps}
            allowClear
            onChange={(v) => onSelect(v, i)}
            options={(i as FiltersFieldSelectType).options}
          />
        ),
      }

      return FIELDS_MAP[i.type]
    }

    const change = (values: any) => {
      const result = Object.entries(values).reduce(
        (acc: Record<string, unknown>, [key, v]: [string, unknown]) => {
          const field = fields.find(({ name }) => name === key)
          if (field && v) {
            acc[key] = field.prepareValue ? field.prepareValue(v) : v
          } else {
            acc[key] = v || undefined
          }

          return acc
        },
        {},
      )

      onChange(result)
    }

    const onSearch = (value: any, f: FiltersFieldType) => {
      change({ ...form.getFieldsValue(), [f.name]: value || undefined })
    }

    const onSelect = (value: any, f: FiltersFieldType) => {
      change({ ...form.getFieldsValue(), [f.name]: value })
    }

    const clear = () => {
      form.resetFields()

      change(form.getFieldsValue())
    }

    return (
      <Form
        form={form}
        id="GENERATED_ID_TABLEFILTER_FORM_VOID_1"
        layout="vertical"
        size={layout.componentSize}
        style={{ width: "100%" }}
      >
        <Flex gap={16}>
          <Row align="bottom" gutter={layout.gutter} style={{ width: "100%" }}>
            {visibleFields.map((i, index) => (
              <Col
                key={`${i.name}__${i.type}__${(i.col ?? 0) + index}`}
                span={layout.filterSpan}
              >
                <Form.Item
                  id="GENERATED_ID_TABLEFILTER_FORM_ITEM_VOID_1"
                  initialValue={state[i.name]}
                  label={i.label}
                  name={i.name}
                  validateTrigger="onChange"
                >
                  {getField(i)}
                </Form.Item>
              </Col>
            ))}
          </Row>
          {hasHiddenFields && (
            <Form.Item label={" "}>
              <Button
                aria-label={isExpanded ? "Hide filters" : "Show filters"}
                icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setIsExpanded((value) => !value)}
                title={isExpanded ? "Hide filters" : "Show filters"}
                type="default"
              />
            </Form.Item>
          )}
        </Flex>

        {clearAll && (
          <Row gutter={layout.gutter} justify="end" style={{ width: "100%" }}>
            <Col>
              <Button icon={<CloseCircleOutlined />} onClick={clear}>
                Clear All
              </Button>
            </Col>
          </Row>
        )}
      </Form>
    )
  },
)
